import { isValidPhoneNumber } from "react-phone-number-input";
import {
  EMPLOYMENT_TYPE_CONSULTANT,
  EMPLOYMENT_TYPE_PROJECT_EMPLOYEE,
  EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
  EMPLOYMENT_TYPE_TRAINEE,
} from "model/OrganizationalStructure";
import { PaymentMethod } from "model/Gig";
import { WorkerEmploymentType } from "model/Salary";
import type {
  AddStaffFieldState,
  AddStaffFieldStates,
  AddStaffFormData,
  AddStaffFormMode,
  AddStaffValidationRequirements,
} from "./addStaffPanel.contract";
import {
  isPaymentMethodValueValidOrEmpty,
  toPaymentMethodValidationTranslationKey,
  validatePaymentMethodValue,
} from "../shared/staff.paymentMethod.logic";
import { getAddStaffFieldVisibility } from "./addStaffPanel.fieldVisibility.logic";
import {
  getEmployeeNumberValidationError,
  getEmploymentDaysValidationError,
  getNameValidationError,
  getOptionalEmailValidationError,
  getOptionalPersonalIdentityNumberValidationError,
  getOptionalTextValidationError,
  getWorkPercentageValidationError,
  hasValidEmployeeNumber,
  hasValue,
  isValidName,
  isValidOptionalEmail,
  isValidOptionalEmploymentDays,
  isValidOptionalPersonalIdentityNumber,
  isValidOptionalText,
  isValidRequiredText,
  isValidWorkPercentage,
} from "../shared/staffInputValidation.logic";

const MONTHLY_SALARY_TYPE = "Monthly";

const GENERIC_VALID_STATE: AddStaffFieldState = { valid: true };
const GENERIC_INVALID_STATE: AddStaffFieldState = { valid: false };

interface AddStaffValidationParams {
  readonly canSelectSimflexityEmployee?: boolean;
  readonly canSelectSimflexityPay?: boolean;
  readonly formData: AddStaffFormData;
  readonly hasVerifiedPhoneNumber: boolean;
  readonly mode: AddStaffFormMode;
  readonly requirements: AddStaffValidationRequirements;
}

interface EmploymentTypeFlags {
  readonly isConsultant: boolean;
  readonly isIntroductoryEmployee: boolean;
  readonly isProjectEmployee: boolean;
  readonly isSimflexityEmployee: boolean;
  readonly isTrainee: boolean;
}

function isPositiveNumber(value?: string): boolean {
  if (!value || value.trim().length === 0) return false;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0;
}

function isSimflexityEmployee(formData: AddStaffFormData): boolean {
  return formData.employmentType === EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE;
}

function validatePhoneNumber({
  formData,
  hasVerifiedPhoneNumber,
}: {
  readonly formData: AddStaffFormData;
  readonly hasVerifiedPhoneNumber: boolean;
}): AddStaffFieldState {
  return {
    valid: hasVerifiedPhoneNumber && isValidPhoneNumber(formData.phoneNumber),
  };
}

function validateName({
  field,
  formData,
}: {
  readonly field: "firstName" | "lastName";
  readonly formData: AddStaffFormData;
}): AddStaffFieldState {
  const value = formData[field];
  if (isValidName(value)) return GENERIC_VALID_STATE;
  return {
    valid: false,
    errorTranslationKey: getNameValidationError({ field, value })
      ?.translationKey,
  };
}

function validateEmail(formData: AddStaffFormData): AddStaffFieldState {
  if (isValidOptionalEmail(formData.email)) return GENERIC_VALID_STATE;
  return {
    valid: false,
    errorTranslationKey: getOptionalEmailValidationError(formData.email)
      ?.translationKey,
  };
}

function validatePersonalIdentityNumber(
  formData: AddStaffFormData,
): AddStaffFieldState {
  const value = formData.personalIdentityNumber;
  if (isValidOptionalPersonalIdentityNumber(value)) return GENERIC_VALID_STATE;
  return {
    valid: false,
    errorTranslationKey:
      getOptionalPersonalIdentityNumberValidationError(value)?.translationKey,
  };
}

function validateEmployeeNumber({
  formData,
  isRequired,
}: {
  readonly formData: AddStaffFormData;
  readonly isRequired: boolean;
}): AddStaffFieldState {
  const value = formData.employeeNumber;
  if (hasValidEmployeeNumber({ isRequired, value })) return GENERIC_VALID_STATE;
  return {
    valid: false,
    errorTranslationKey:
      getEmployeeNumberValidationError(value)?.translationKey,
  };
}

function validateRole(formData: AddStaffFormData): AddStaffFieldState {
  if (isValidRequiredText(formData.role)) return GENERIC_VALID_STATE;
  return {
    valid: false,
    errorTranslationKey: getOptionalTextValidationError({
      translationKey: "Staff.AddStaffPanelRoleTooLong",
      value: formData.role,
    })?.translationKey,
  };
}

function validateTeamIds({
  formData,
  isRequired,
}: {
  readonly formData: AddStaffFormData;
  readonly isRequired: boolean;
}): AddStaffFieldState {
  return { valid: !isRequired || formData.teamIds.length > 0 };
}

function validateStartDate(formData: AddStaffFormData): AddStaffFieldState {
  return { valid: formData.startDate != null };
}

function validateEndDate({
  formData,
  isRequired,
}: {
  readonly formData: AddStaffFormData;
  readonly isRequired: boolean;
}): AddStaffFieldState {
  if (formData.endDate == null) {
    return isRequired ? GENERIC_INVALID_STATE : GENERIC_VALID_STATE;
  }
  if (formData.startDate == null) return GENERIC_VALID_STATE;
  return { valid: formData.endDate.isAfter(formData.startDate) };
}

function validateEmploymentType({
  canSelectSimflexityEmployee,
  formData,
}: {
  readonly canSelectSimflexityEmployee: boolean;
  readonly formData: AddStaffFormData;
}): AddStaffFieldState {
  if (!hasValue(formData.employmentType)) return GENERIC_INVALID_STATE;
  return {
    valid: !isSimflexityEmployee(formData) || canSelectSimflexityEmployee,
  };
}

function validateEmployeeCategory({
  formData,
  isRequired,
}: {
  readonly formData: AddStaffFormData;
  readonly isRequired: boolean;
}): AddStaffFieldState {
  return { valid: !isRequired || hasValue(formData.employeeCategory) };
}

function validateSalaryType({
  formData,
  isRequired,
}: {
  readonly formData: AddStaffFormData;
  readonly isRequired: boolean;
}): AddStaffFieldState {
  if (isRequired && !hasValue(formData.salaryType))
    return GENERIC_INVALID_STATE;
  return {
    valid:
      !isSimflexityEmployee(formData) ||
      formData.salaryType !== MONTHLY_SALARY_TYPE,
  };
}

function validateSalaryAmount({
  formData,
  isRequired,
}: {
  readonly formData: AddStaffFormData;
  readonly isRequired: boolean;
}): AddStaffFieldState {
  return { valid: !isRequired || isPositiveNumber(formData.salaryAmount) };
}

function validateWorkPercentage({
  formData,
  isRequired,
}: {
  readonly formData: AddStaffFormData;
  readonly isRequired: boolean;
}): AddStaffFieldState {
  const value = formData.workPercentage;
  if (!isRequired || isValidWorkPercentage(value)) return GENERIC_VALID_STATE;
  return {
    valid: false,
    errorTranslationKey:
      getWorkPercentageValidationError(value)?.translationKey,
  };
}

function validateTaxTable({
  formData,
  isRequired,
}: {
  readonly formData: AddStaffFormData;
  readonly isRequired: boolean;
}): AddStaffFieldState {
  return { valid: !isRequired || hasValue(formData.taxTable) };
}

function validateAgreement({
  formData,
  isAsked,
}: {
  readonly formData: AddStaffFormData;
  readonly isAsked: boolean;
}): AddStaffFieldState {
  const value = formData.agreement;
  if (!isAsked || isValidOptionalText(value)) return GENERIC_VALID_STATE;
  return {
    valid: false,
    errorTranslationKey: getOptionalTextValidationError({
      translationKey: "Staff.AddStaffPanelAgreementTooLong",
      value,
    })?.translationKey,
  };
}

function validateWorkplace({
  formData,
  isAsked,
}: {
  readonly formData: AddStaffFormData;
  readonly isAsked: boolean;
}): AddStaffFieldState {
  const value = formData.workplace;
  if (!isAsked || isValidOptionalText(value)) return GENERIC_VALID_STATE;
  return {
    valid: false,
    errorTranslationKey: getOptionalTextValidationError({
      translationKey: "Staff.AddStaffPanelWorkplaceTooLong",
      value,
    })?.translationKey,
  };
}

function validateEmploymentDays({
  isAsked,
  value,
}: {
  readonly isAsked: boolean;
  readonly value: string;
}): AddStaffFieldState {
  if (!isAsked || isValidOptionalEmploymentDays(value))
    return GENERIC_VALID_STATE;
  return {
    valid: false,
    errorTranslationKey:
      getEmploymentDaysValidationError(value)?.translationKey,
  };
}

function validatePaymentType({
  canSelectSimflexityPay,
  formData,
  isRequired,
}: {
  readonly canSelectSimflexityPay: boolean;
  readonly formData: AddStaffFormData;
  readonly isRequired: boolean;
}): AddStaffFieldState {
  if (isRequired && !hasValue(formData.paymentType))
    return GENERIC_INVALID_STATE;
  if (isSimflexityEmployee(formData)) {
    return { valid: formData.paymentType === PaymentMethod.SIMFLEXITY_PAY };
  }
  return {
    valid:
      formData.paymentType !== PaymentMethod.SIMFLEXITY_PAY ||
      canSelectSimflexityPay,
  };
}

function validatePaymentMethod({
  formData,
  isAsked,
}: {
  readonly formData: AddStaffFormData;
  readonly isAsked: boolean;
}): AddStaffFieldState {
  const { paymentMethodType, paymentMethodValue } = formData;
  if (
    !isAsked ||
    isPaymentMethodValueValidOrEmpty(paymentMethodType, paymentMethodValue)
  ) {
    return GENERIC_VALID_STATE;
  }
  const result = validatePaymentMethodValue(
    paymentMethodType,
    paymentMethodValue,
  );
  if (result.type === "success") return GENERIC_VALID_STATE;
  return {
    valid: false,
    errorTranslationKey: toPaymentMethodValidationTranslationKey(result.error),
  };
}

function getEmploymentTypeFlags(
  formData: AddStaffFormData,
): EmploymentTypeFlags {
  const employmentType = formData.employmentType;
  return {
    isConsultant: employmentType === EMPLOYMENT_TYPE_CONSULTANT,
    isIntroductoryEmployee:
      employmentType === WorkerEmploymentType.INTRODUCTORY,
    isProjectEmployee: employmentType === EMPLOYMENT_TYPE_PROJECT_EMPLOYEE,
    isSimflexityEmployee:
      employmentType === EMPLOYMENT_TYPE_SIMFLEXITY_EMPLOYEE,
    isTrainee: employmentType === EMPLOYMENT_TYPE_TRAINEE,
  };
}

export function getAddStaffValidationRequirements(
  formData: AddStaffFormData,
  mode: AddStaffFormMode,
): AddStaffValidationRequirements {
  const employmentTypeFlags = getEmploymentTypeFlags(formData);
  const isEmployeeNumberRequired =
    !employmentTypeFlags.isConsultant &&
    !employmentTypeFlags.isTrainee &&
    !employmentTypeFlags.isSimflexityEmployee;
  // Hidden fields are never required, so requirements follow field visibility.
  const visibility = getAddStaffFieldVisibility({
    employmentType: formData.employmentType,
    mode,
    salaryType: formData.salaryType,
  });

  return {
    isEmployeeNumberRequired,
    isEmployeeCategoryRequired: visibility.showEmployeeCategory,
    isSalaryModelRequired: visibility.showSalaryType,
    isSalaryAmountRequired: visibility.showSalary,
    isWorkPercentageRequired:
      visibility.showSalaryType && formData.salaryType === MONTHLY_SALARY_TYPE,
    isTaxTableRequired: visibility.showTaxTable,
    isTeamRequired: visibility.showTeam,
    isPaymentTypeRequired: visibility.showPaymentType,
    isEndDateRequired:
      employmentTypeFlags.isIntroductoryEmployee ||
      employmentTypeFlags.isProjectEmployee ||
      employmentTypeFlags.isTrainee,
  };
}

/**
 * What the form makes of each field, asked one field at a time so the answer
 * names the input that has to change and carries the message it should show.
 *
 * A field a requirement does not ask for, or the employment type hides, has
 * nothing to answer for and is valid. Company policy is spread across the three
 * fields it constrains rather than kept as a rule of its own, so a form that
 * breaks it points at the fields that break it.
 */
export function getAddStaffFieldStates({
  canSelectSimflexityEmployee = true,
  canSelectSimflexityPay = false,
  formData,
  hasVerifiedPhoneNumber,
  mode,
  requirements,
}: AddStaffValidationParams): AddStaffFieldStates {
  const fieldVisibility = getAddStaffFieldVisibility({
    employmentType: formData.employmentType,
    mode,
    salaryType: formData.salaryType,
  });
  const showsContractTerms = fieldVisibility.showContractTerms;
  const showsVacationEarningDays = fieldVisibility.showVacationEarningDays;

  return {
    phoneNumber: validatePhoneNumber({ formData, hasVerifiedPhoneNumber }),
    firstName: validateName({ field: "firstName", formData }),
    lastName: validateName({ field: "lastName", formData }),
    email: validateEmail(formData),
    personalIdentityNumber: validatePersonalIdentityNumber(formData),
    employeeNumber: validateEmployeeNumber({
      formData,
      isRequired: requirements.isEmployeeNumberRequired,
    }),

    role: validateRole(formData),
    teamIds: validateTeamIds({
      formData,
      isRequired: requirements.isTeamRequired,
    }),
    employmentType: validateEmploymentType({
      canSelectSimflexityEmployee,
      formData,
    }),
    employeeCategory: validateEmployeeCategory({
      formData,
      isRequired: requirements.isEmployeeCategoryRequired,
    }),
    startDate: validateStartDate(formData),
    endDate: validateEndDate({
      formData,
      isRequired: requirements.isEndDateRequired,
    }),
    salaryType: validateSalaryType({
      formData,
      isRequired: requirements.isSalaryModelRequired,
    }),
    salaryAmount: validateSalaryAmount({
      formData,
      isRequired: requirements.isSalaryAmountRequired,
    }),
    workPercentage: validateWorkPercentage({
      formData,
      isRequired: requirements.isWorkPercentageRequired,
    }),
    taxTable: validateTaxTable({
      formData,
      isRequired: requirements.isTaxTableRequired,
    }),

    agreement: validateAgreement({ formData, isAsked: showsContractTerms }),
    noticeDays: validateEmploymentDays({
      isAsked: showsContractTerms,
      value: formData.noticeDays,
    }),
    vacationDays: validateEmploymentDays({
      isAsked: showsContractTerms,
      value: formData.vacationDays,
    }),
    workplace: validateWorkplace({ formData, isAsked: showsContractTerms }),

    maxSickLeaveDays: validateEmploymentDays({
      isAsked: showsVacationEarningDays,
      value: formData.maxSickLeaveDays,
    }),
    maxCareOfChildDays: validateEmploymentDays({
      isAsked: showsVacationEarningDays,
      value: formData.maxCareOfChildDays,
    }),
    maxParentalLeaveDays: validateEmploymentDays({
      isAsked: showsVacationEarningDays,
      value: formData.maxParentalLeaveDays,
    }),
    advanceVacationDays: validateEmploymentDays({
      isAsked: showsVacationEarningDays,
      value: formData.advanceVacationDays,
    }),

    paymentType: validatePaymentType({
      canSelectSimflexityPay,
      formData,
      isRequired: requirements.isPaymentTypeRequired,
    }),
    paymentMethodValue: validatePaymentMethod({
      formData,
      isAsked: fieldVisibility.showPaymentMethod,
    }),
  };
}
